import React, { useState, useEffect } from 'react';
import NovoEventoService from '../../services/Eventos/NovoEventoService';
import MapaTrajetoCorrida from './MapaTrajetoCorrida';
import { Button, Spinner } from '../ui';
import { UploadCloud, Trash2, MapPin, CheckCircle, AlertTriangle, HelpCircle } from 'lucide-react';

export const UploadTrajetoMaps = ({
    idCorrida,
    trajetoAtual,
    onTrajetoAtualizado,
}) => {
    const [trajeto, setTrajeto] = useState(trajetoAtual || null);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);
    const [sucesso, setSucesso] = useState(null);

    useEffect(() => {
        setTrajeto(trajetoAtual || null);
        setErro(null);
        setSucesso(null);
    }, [trajetoAtual, idCorrida]);

    const eventoService = new NovoEventoService();

    const handleUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validação da extensão (.kml, .kmz, .gpx)
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!['kml', 'kmz', 'gpx'].includes(ext || '')) {
            setErro('Por favor, selecione um arquivo válido com extensão .kml, .kmz ou .gpx.');
            return;
        }

        // Validação de tamanho máximo (5 MB)
        const maxBytes = 5 * 1024 * 1024;
        if (file.size > maxBytes) {
            setErro('O arquivo selecionado excede o limite máximo permitido de 5 MB.');
            return;
        }

        setLoading(true);
        setErro(null);
        setSucesso(null);

        const formData = new FormData();
        formData.append('arquivo', file);

        try {
            const response = await eventoService.uploadTrajeto(idCorrida, formData);
            const data = await response.json().catch(() => null);

            if (!response.ok) {
                const msg = data?.erro || data?.arquivo?.[0] || data?.detail || 'Falha ao processar arquivo de trajeto.';
                setErro(msg);
                return;
            }

            const novoTrajeto = data?.trajeto_mapa || null;
            setTrajeto(novoTrajeto);
            if (onTrajetoAtualizado) {
                onTrajetoAtualizado(novoTrajeto);
            }
            setSucesso(data?.mensagem || 'Percurso carregado e traçado no Google Maps com sucesso!');
        } catch (err) {
            console.error('Erro no upload do trajeto:', err);
            setErro('Não foi possível conectar ao servidor para processar o percurso.');
        } finally {
            setLoading(false);
            // Reseta o input para permitir selecionar o mesmo arquivo novamente se desejado
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    const handleRemover = async () => {
        if (!window.confirm('Deseja realmente remover o trajeto do mapa desta corrida?')) return;

        setLoading(true);
        setErro(null);
        setSucesso(null);

        try {
            const response = await eventoService.removerTrajeto(idCorrida);
            const data = await response.json().catch(() => null);

            if (!response.ok) {
                setErro(data?.erro || data?.detail || 'Não foi possível remover o trajeto.');
                return;
            }

            setTrajeto(null);
            if (onTrajetoAtualizado) {
                onTrajetoAtualizado(null);
            }
            setSucesso('Trajeto removido com sucesso.');
        } catch (err) {
            console.error('Erro ao remover trajeto:', err);
            setErro('Falha de conexão ao tentar remover o trajeto.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-urb-card p-6 rounded-2xl border border-urb-border space-y-6 text-white">
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    <span className="p-2 rounded-lg bg-urb-primary/10 text-urb-primary border border-urb-primary/20">
                        <MapPin className="w-5 h-5" />
                    </span>
                    <div>
                        <h3 className="text-lg font-bold text-white">Mapa do Percurso (Google Maps)</h3>
                        <p className="text-xs sm:text-sm text-slate-400">
                            Envie o percurso oficial da prova exportado do <strong>Google My Maps</strong> ou <strong>Google Earth</strong> (.kml, .kmz ou .gpx).
                        </p>
                    </div>
                </div>
            </div>

            {erro && (
                <div className="p-4 bg-red-950/40 border border-red-800/60 rounded-xl text-sm text-red-300 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <div className="flex-1 leading-relaxed">{erro}</div>
                </div>
            )}

            {sucesso && (
                <div className="p-4 bg-emerald-950/40 border border-emerald-800/60 rounded-xl text-sm text-emerald-300 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <div className="flex-1 leading-relaxed">{sucesso}</div>
                </div>
            )}

            {/* Área de Seleção de Arquivo */}
            <div className="border-2 border-dashed border-urb-border hover:border-urb-primary/70 bg-slate-900/40 rounded-2xl p-6 text-center transition-all">
                <input
                    type="file"
                    id={`upload-trajeto-${idCorrida}`}
                    accept=".kml,.kmz,.gpx"
                    onChange={handleUpload}
                    disabled={loading}
                    className="hidden"
                />
                <label
                    htmlFor={`upload-trajeto-${idCorrida}`}
                    className={`cursor-pointer inline-flex flex-col items-center justify-center space-y-3 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    <div className="w-14 h-14 bg-urb-primary/10 text-urb-primary border border-urb-primary/20 rounded-2xl flex items-center justify-center transition-transform hover:scale-105">
                        {loading ? <Spinner size="md" /> : <UploadCloud className="w-7 h-7" />}
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-urb-primary hover:underline">
                            {loading ? 'Processando percurso...' : 'Clique para selecionar o arquivo .KML, .KMZ ou .GPX'}
                        </span>
                        <span className="text-xs text-slate-400">
                            Suporta arquivos Google My Maps / GPS (Tamanho máximo: 5 MB)
                        </span>
                    </div>
                </label>
            </div>

            {/* Instruções do Google My Maps */}
            <details className="text-xs text-slate-400 bg-slate-900/60 p-4 rounded-xl border border-urb-border group">
                <summary className="cursor-pointer font-semibold text-slate-300 flex items-center gap-2 select-none hover:text-white">
                    <HelpCircle className="w-4 h-4 text-urb-primary" />
                    <span>Como criar e exportar o percurso no Google My Maps?</span>
                </summary>
                <ol className="list-decimal list-inside mt-3 space-y-2 pl-2 text-slate-400 border-t border-urb-border pt-3 leading-relaxed">
                    <li>
                        Acesse <a href="https://mymaps.google.com" target="_blank" rel="noreferrer" className="text-urb-primary underline hover:text-emerald-300">mymaps.google.com</a> e crie um novo mapa.
                    </li>
                    <li>
                        Clique no ícone <strong>Adicionar trajeto a pé / em linha</strong> e trace as ruas do percurso da corrida.
                    </li>
                    <li>
                        No menu de opções (ícone de 3 pontos verticais ao lado do título do mapa), selecione <strong>Exportar para KML/KMZ</strong>.
                    </li>
                    <li>
                        Marque a caixa da camada do trajeto e faça o download. Depois envie o arquivo baixado aqui!
                    </li>
                </ol>
            </details>

            {/* Preview Interativo do Mapa se já houver trajeto salvo */}
            {trajeto && (
                <div className="space-y-3 pt-4 border-t border-urb-border">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                            <span>Arquivo ativo: <strong className="text-white">{trajeto.nome_arquivo || 'trajeto'}</strong></span>
                            {trajeto.distancia_km && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-urb-border">
                                    {trajeto.distancia_km} km
                                </span>
                            )}
                        </div>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={handleRemover}
                            disabled={loading}
                            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            Remover percurso
                        </Button>
                    </div>

                    <MapaTrajetoCorrida trajeto={trajeto} altura="380px" />
                </div>
            )}
        </div>
    );
};

export default UploadTrajetoMaps;
